import { defineComponent, ref, Ref, watch } from 'vue';
import { IBannerImageManager } from '@truckdown/locations';
import context from '@truckdown/components-systems';

interface UploadFile {
    file: File;
    preview: string;
    status: 'pending' | 'success' | 'error';
    id: string
}
interface IData {
    files: Ref<UploadFile[]>;
    isDragging: Ref<boolean>;
    errorMessage: Ref<string>;
    fileInput: Ref<HTMLInputElement | null>,
}

const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const getInitialData = function (props: any): IData {
    return {
        files: ref<UploadFile[]>([]),
        isDragging: ref<boolean>(false),
        errorMessage: ref<string>(""),
        fileInput: ref<HTMLInputElement | null>(null)
    };
};

const getComputed = function (props: any, data: IData) {
    return {};
};

const getMethods = function (props: any, data: IData, ctx: any) {
    function isFileWithinSizeLimit(uploadFile: UploadFile): boolean {
        return uploadFile.file.size <= MAX_FILE_SIZE_BYTES;
    }
    const upload = async (uploadFile: UploadFile) => {
        const index = data.files.value.findIndex(f => f.file === uploadFile.file);
        if (index === -1) return;
        const reactiveFile = data.files.value[index];

        if (!isFileWithinSizeLimit(uploadFile)) {
            data.errorMessage.value = "Max file size is 5MB.";
            reactiveFile.status = 'error';
            return;
        }
       
        const manager = await context.getService<IBannerImageManager>('IBannerImageManager');
        try {
            reactiveFile.status = 'pending';
            let result;

            result = await manager.uploadImage(reactiveFile.file, props.accountId);
           
            if (result && result.success) {
                reactiveFile.id = result.id;
                reactiveFile.status = 'success';
            } else if (result && result.message) {
                data.errorMessage.value = result.message;
                reactiveFile.status = 'error';
            }

        } catch (error) {
            console.error('Upload failed:', error);
            reactiveFile.status = 'error';
        }
    };

    return {
        onDrop: function (event: DragEvent) {
            event.preventDefault();
            data.isDragging.value = false;
            if (!event.dataTransfer) return;
            const droppedFiles = Array.from(event.dataTransfer.files);
            this.uploadFiles(droppedFiles);
        },

        onFileChange: function (event: Event) {
            const input = event.target as HTMLInputElement;
            if (input.files) {
                this.uploadFiles(Array.from(input.files));
            }
        },

        uploadFiles: async function (files: File[]) {
            ctx.emit('start-upload');
            const mapped: UploadFile[] = files.map(file => ({
                file,
                preview: URL.createObjectURL(file),
                status: 'pending',
                id: ""
            }));

            data.files.value = [mapped[0]];

            const uploadPromises = mapped.map(uploadFile => upload(uploadFile));
            await Promise.all(uploadPromises);
            ctx.emit('files-uploaded', mapped);
        },

        onDragOver: function (event: DragEvent) {
            event.preventDefault();
            data.isDragging.value = true;
        },

        onDragLeave: function () {
            data.isDragging.value = false;
        },

        triggerFileSelect: function () {
            data.fileInput.value?.click();
        },

        deleteUploaded: function () {

        }
    };
};

export default defineComponent({
    name: 'DropzoneUploader',
    props: {
        removeId: {
            type: String,
            required: false,
        },
        accountId: {
            type: String,
            required: true,
        },
        isBanner: {
            type: Boolean,
            required: false,
        }
    },
    emits: ['files-uploaded', 'start-upload'],
    setup: function (props, ctx) {
        let data = getInitialData(props);
        let computedValues = getComputed(props, data);
        let methods = getMethods(props, data, ctx);

        const removeImage = (id: string) => {
            const files = data.files.value;
            const file = files.find(file => file.id === id);
            if (file) {
                if (file.preview) URL.revokeObjectURL(file.preview);
                data.files.value = files.filter(f => f.id !== id);
            }
        };

        watch(() => props.removeId, (newVal, oldVal) => {
            if (newVal && oldVal !== newVal) {
                data.files.value = data.files.value.filter(item => item.id !== newVal);
                removeImage(newVal);
            }
        });

        return {
            ...data,
            ...computedValues,
            ...methods,
        };
    }
});
